import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ArrowLeft, PackagePlus } from "lucide-react";
import { toast } from "sonner";

// Détail d'un export CSV — filtrage en cascade Allée -> Colonne : sélectionner
// une allée affiche tous les articles de cette allée, puis sélectionner une
// colonne restreint encore à cette colonne précise. Tant qu'aucune allée
// n'est choisie, le tableau (potentiellement plusieurs milliers de lignes)
// n'est pas affiché en entier — uniquement un message d'invite.
export default function DepotCsvDetailPage() {
  const { id } = useParams();
  const [exportDoc, setExportDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allee, setAllee] = useState("");
  const [colonne, setColonne] = useState("");

  // Bouton "Article manquant" : un article physiquement présent dans le
  // tiroir ouvert mais absent du fichier CSV importé. L'allée et la colonne
  // sont automatiquement reprises du filtre en cours (là où le tiroir vient
  // d'être ouvert) — seuls le tiroir/la position (et bien sûr le nom/l'ID)
  // restent à saisir.
  const [manquantDialog, setManquantDialog] = useState(false);
  const [manquantNom, setManquantNom] = useState("");
  const [manquantUgs, setManquantUgs] = useState("");
  const [manquantTiroir, setManquantTiroir] = useState("");
  const [manquantPosition, setManquantPosition] = useState("");
  const [manquantSaving, setManquantSaving] = useState(false);

  const openManquantDialog = () => {
    setManquantNom("");
    setManquantUgs("");
    setManquantTiroir("");
    setManquantPosition("");
    setManquantDialog(true);
  };

  const confirmManquant = async () => {
    if (!manquantNom.trim()) { toast.error("Le nom de l'article est obligatoire"); return; }
    setManquantSaving(true);
    try {
      const { data } = await api.post(`/depot/csv-exports/${id}/manquant`, {
        nom: manquantNom.trim(), ugs: manquantUgs.trim(),
        allee, colonne, tiroir: manquantTiroir.trim(), position: manquantPosition.trim(),
      });
      setExportDoc((prev) => ({
        ...prev,
        lignes: [...prev.lignes, data.ligne],
        nb_lignes: (prev.nb_lignes || prev.lignes.length) + 1,
      }));
      toast.success("Article manquant ajouté à la liste");
      setManquantDialog(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setManquantSaving(false);
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get(`/depot/csv-exports/${id}`);
      setExportDoc(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Chaque ligne garde son index D'ORIGINE (position dans le tableau complet
  // renvoyé par le serveur) même après filtrage, pour que les mises à jour
  // (PATCH .../lignes/{index}) ciblent toujours la bonne ligne.
  const lignesIndexees = useMemo(
    () => (exportDoc?.lignes || []).map((l, index) => ({ ...l, _index: index })),
    [exportDoc],
  );

  const alleeOptions = useMemo(
    () => [...new Set(lignesIndexees.map((l) => l.allee).filter(Boolean))].sort(),
    [lignesIndexees],
  );
  const colonneOptions = useMemo(
    () => [...new Set(lignesIndexees.filter((l) => l.allee === allee).map((l) => l.colonne).filter(Boolean))]
      .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0)),
    [lignesIndexees, allee],
  );

  const lignesAffichees = useMemo(() => {
    if (!allee) return [];
    return lignesIndexees
      .filter((l) => l.allee === allee && (!colonne || l.colonne === colonne))
      // Tri du tiroir le plus petit au plus grand (demande explicite) —
      // numérique, pas alphabétique (sinon "10" passerait avant "2").
      .sort((a, b) => (parseFloat(a.tiroir) || 0) - (parseFloat(b.tiroir) || 0));
  }, [lignesIndexees, allee, colonne]);

  const updateLigne = async (index, patch) => {
    try {
      await api.patch(`/depot/csv-exports/${id}/lignes/${index}`, patch);
      setExportDoc((prev) => {
        const lignes = [...prev.lignes];
        lignes[index] = { ...lignes[index], ...patch };
        return { ...prev, lignes };
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <Layout title="Inventaire CSV"><p className="text-sm text-muted-foreground">Chargement...</p></Layout>;
  if (!exportDoc) return null;

  return (
    <Layout title={exportDoc.nom_fichier}>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Link to="/depot/inventaire">
          <Button variant="ghost" size="icon" data-testid="depot-csv-detail-back"><ArrowLeft size={18} /></Button>
        </Link>
        <div className="min-w-0">
          <h1 className="font-heading text-lg font-semibold truncate">{exportDoc.nom_fichier}</h1>
          <p className="text-xs text-muted-foreground">{exportDoc.lignes.length} article(s) au total</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="w-40">
          <label className="text-xs font-medium text-muted-foreground block mb-1">Allée</label>
          <Select value={allee} onValueChange={(v) => { setAllee(v); setColonne(""); }}>
            <SelectTrigger data-testid="depot-csv-filter-allee"><SelectValue placeholder="Sélectionner allée" /></SelectTrigger>
            <SelectContent>
              {alleeOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="text-xs font-medium text-muted-foreground block mb-1">Colonne</label>
          <Select value={colonne} onValueChange={setColonne} disabled={!allee}>
            <SelectTrigger data-testid="depot-csv-filter-colonne"><SelectValue placeholder="Sélectionner colonne" /></SelectTrigger>
            <SelectContent>
              {colonneOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {allee && (
          <Button variant="ghost" size="sm" className="self-end" onClick={() => { setAllee(""); setColonne(""); }} data-testid="depot-csv-filter-reset">
            Réinitialiser
          </Button>
        )}
        {allee && colonne && (
          <Button variant="outline" size="sm" className="self-end gap-1.5" onClick={openManquantDialog} data-testid="depot-csv-manquant-button">
            <PackagePlus size={14} /> Article manquant
          </Button>
        )}
      </div>

      {!allee && (
        <p className="text-sm text-muted-foreground" data-testid="depot-csv-select-allee-hint">
          Sélectionnez une allée pour afficher les articles à vérifier.
        </p>
      )}

      {allee && (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-sm" data-testid="depot-csv-table">
            <thead className="bg-secondary text-left">
              <tr>
                <th className="px-3 py-2 font-medium">Vérifier</th>
                <th className="px-3 py-2 font-medium">Nom</th>
                <th className="px-3 py-2 font-medium">Quantité</th>
                <th className="px-3 py-2 font-medium">ID</th>
                <th className="px-3 py-2 font-medium">Allée</th>
                <th className="px-3 py-2 font-medium">Colonne</th>
                <th className="px-3 py-2 font-medium">Tiroir</th>
                <th className="px-3 py-2 font-medium">Position</th>
              </tr>
            </thead>
            <tbody>
              {lignesAffichees.map((l) => {
                // Un article ajouté via "Article manquant" (trouvé dans le
                // tiroir mais absent du fichier CSV d'origine) reste en rouge
                // en permanence, quel que soit son état de vérification — la
                // couleur signale ici sa PROVENANCE (ajout manuel terrain),
                // pas un écart de quantité (déjà couvert par l'orange).
                const rowClass = l.manquant
                  ? "bg-red-500/10"
                  : l.verifie
                    ? "bg-emerald-500/10"
                    : l.quantite_modifiee !== null && l.quantite_modifiee !== undefined
                      ? "bg-amber-500/10"
                      : "";
                return (
                  <tr key={l._index} className={`border-t border-border ${rowClass}`} data-testid={`depot-csv-row-${l._index}`}>
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={!!l.verifie}
                        onCheckedChange={(v) => updateLigne(l._index, { verifie: !!v })}
                        data-testid={`depot-csv-verifie-${l._index}`}
                      />
                    </td>
                    <td className="px-3 py-2">{l.nom}</td>
                    <td className="px-3 py-2 w-24">
                      <Input
                        type="number"
                        className="h-8 w-20"
                        value={l.quantite_modifiee ?? l.quantite}
                        onChange={(e) => {
                          const val = e.target.value === "" ? null : parseInt(e.target.value, 10);
                          const modifiee = val === null || val === l.quantite ? null : val;
                          updateLigne(l._index, { quantite_modifiee: modifiee });
                        }}
                        // Bug corrigé (inventaire sur tablette) : au clic/tap dans le
                        // champ, on sélectionne TOUT le nombre existant plutôt que de
                        // placer le curseur à l'endroit touché — sans quoi, sur
                        // tablette, une suppression n'efface que le chiffre sous le
                        // curseur (ex. sur "50", rien ne se passait si le doigt touchait
                        // avant le "5"). Un tap suivi d'une saisie ou d'un retour arrière
                        // remplace maintenant directement toute la quantité.
                        onFocus={(e) => e.target.select()}
                        onClick={(e) => e.target.select()}
                        data-testid={`depot-csv-quantite-${l._index}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{l.ugs}</td>
                    <td className="px-3 py-2">{l.allee}</td>
                    <td className="px-3 py-2">{l.colonne}</td>
                    <td className="px-3 py-2">{l.tiroir}</td>
                    <td className="px-3 py-2">{l.position}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={manquantDialog} onOpenChange={setManquantDialog}>
        <DialogContent data-testid="depot-csv-manquant-dialog">
          <DialogHeader><DialogTitle>Article manquant</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Article trouvé dans le tiroir mais absent de la liste — l'allée et la colonne sont reprises du
              filtre en cours, indiquez au moins le tiroir et la position.
            </p>
            <Input
              autoFocus
              placeholder="Nom de l'article"
              value={manquantNom} onChange={(e) => setManquantNom(e.target.value)}
              data-testid="depot-csv-manquant-nom"
            />
            <Input
              placeholder="ID / référence (optionnel)"
              value={manquantUgs} onChange={(e) => setManquantUgs(e.target.value)}
              data-testid="depot-csv-manquant-ugs"
            />
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Allée</label>
                <Input value={allee} disabled data-testid="depot-csv-manquant-allee" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Colonne</label>
                <Input value={colonne} disabled data-testid="depot-csv-manquant-colonne" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Tiroir"
                value={manquantTiroir} onChange={(e) => setManquantTiroir(e.target.value)}
                data-testid="depot-csv-manquant-tiroir"
              />
              <Input
                placeholder="Position"
                value={manquantPosition} onChange={(e) => setManquantPosition(e.target.value)}
                data-testid="depot-csv-manquant-position"
              />
            </div>
          </div>
          <DialogFooter>
            <Button disabled={manquantSaving} onClick={confirmManquant} data-testid="depot-csv-manquant-confirm">
              {manquantSaving ? "Ajout..." : "Valider"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
